/// <reference path="i18n.ts" />
/// <reference path="polyfill.ts" />

module YVR.Utilities.Map {
	// Dependencies
	import i18n = YVR.Utilities.I18n;

	//#region Interfaces

	export interface IMapState {
		facility: number | null;
		keyword: string | null;
		level: string;
		location: string[];
		terminals: string[];
	}

	export interface IElevator {
		EquipmentF: string;
		EquipmentI: string;
		EquipmentN: string;
		EquipmentT: string;
	}

	export interface IEscalator {
		descending: string | null;
		direction: string | null;
		EquipmentF: string;
		EquipmentI: string;
		EquipmentN: string;
		EquipmentT: string;
	}

	export interface IRoad {
		BasemapSecurity: string;
		SPEED: number;
		LastUpdate: string;
		CYCLING: string;
		NAME: string;
		NUMLANES: number;
		ROAD_CLASS: string;
	}

	//#endregion


	//#region Constants

	/**
	 * Consants
	 * @type {Object}
	 */
	export const consts = {
		CATEGORIES: {
			FOOD_DRINK: 'STR_FOOD_DRINK'
		},
		CLASSIFICAIONS: {
			ADMINISTRATION: 'ADMINISTRATION',
			BACKOFHOUSE: 'BACKOFHOUSE',
			COMMERCIAL: 'COMMERCIAL',
			INDUSTRIAL: 'INDUSTRIAL',
			PASSENGER: 'PASSENGER',
			PBS: 'PBS',
			ROADS: 'ROADS',
			WASHROOM: 'WASHROOM'
		},
		COLORS: {
			BLACK: '#000000',
			GREY: '#CED3DA',
			SEA_BLUE: '#005489',
			SKY_BLUE: '#709abd',
			SKY_BLUE_LIGHT: '#D6E2EB'
		},
		PROPERTIES: {
			BUILDING_LEVEL: 'PoiBuildingLevel',
			CATEGORY: 'PoiCategory',
			DESCRIPTION: 'Description',
			KEYWORDS: 'PoiKeywords',
			NAME: 'PoiNameLocID',
			OPENING_HOURS: 'PoiOpeningHours',
			TAGLINE: 'PoiTaglineLocID',
			TERMINAL: 'PoiTerminal',
			SUBCATEGORY: 'PoiSubCategory',
			SUBCATEGORY_DESCRIPTION: 'SubCategoryDescription'
		},
		REGEX: {
			CAROUSEL_NUMBER: /^.*\s+(\d+)$/i,
			GATE_NUMBER: /^.*?(\d+[a-z]*).*$/i
		},
		SECURITY: {
			BEFORE: 'Before',
			AFTER: 'After'
		},
		SUBCATEGORIES: {
			BAGGAGE: 'STR_BAGGAGE_CAROUSELS',
			CAR_RENTAL: 'STR_CAR_RENTAL',
			CHECK_IN: 'STR_CHECK_IN_AREAS',
			CHILDREN_AREA: 'STR_CHILDREN_AREA',
			COURTESY_SHUTTLES: 'STR_COURTESY_SHUTTLES',
			FOOD_COURT: 'STR_FOOD_COURT',
			HOTEL: 'STR_HOTELS_CONFERENCE_FACILITIES',
			INFO: 'STR_INFORMATION_COUNTERS',
			PARKING: 'STR_PARKING',
			PUBLIC_TRANSPORTATION: 'STR_PUBLIC_TRANSPORTATION',
			SECURITY_CHECKPOINTS: 'STR_SECURITY_CHECKPOINTS',
			SEDAN: 'STR_LIMOUSINES_SEDANS',
			TAXIS: 'STR_TAXIS',
			WASHROOMS: 'STR_WASHROOMS'
		},
		TERMINALS: {
			ALL: '',
			ARRIVALS: 'Arrivals',
			DOMESTIC: 'Domestic',
			LINK: 'Link',
			INTERNATIONAL: 'International',
			SOUTH_TERMINAL: 'South Terminal',
			SWING: 'Swing',
			US: 'US',
			USA: 'USA'
		}
	};

	//#endregion


	//#region Configuration

	/**
	 * General configuration.
	 * @type {Object}
	 */
	export const config = {
		center: [49.1958, -123.1795],
		classNames: {
			marker: 'yvr-map-marker'
		},
		clusters: {
			disableClusteringAtZoom: 19,
			maxClusterRadius: 30,
			showCoverageOnHover: false
		},
		colors: {
			base: consts.COLORS.BLACK,
			road: consts.COLORS.GREY,
			active: {
				after: consts.COLORS.SEA_BLUE,
				before: consts.COLORS.SKY_BLUE
			},
			inactive: {
				after: consts.COLORS.SKY_BLUE_LIGHT,
				before: consts.COLORS.SKY_BLUE_LIGHT
			}
		},
		excludedSectors: ['Parkade'],
		facilityPopup: {
			closeButton: false,
			closeOnClick: false,
			keepInView: true,
			autoPanPadding: L.point(60, 60),
			minWidth: 126,
			maxWidth: 156
		},
		fitBounds: {
			animate: false,
			padding: [20, 20]
		} as L.FitBoundsOptions,
		initialState: {
			facility: null,
			keyword: null,
			level: '3',
			//location: consts.CATEGORIES.FOOD_DRINK,
			location: [],
			terminals: [
				consts.TERMINALS.ALL,
				consts.TERMINALS.ARRIVALS,
				consts.TERMINALS.DOMESTIC,
				consts.TERMINALS.LINK,
				consts.TERMINALS.INTERNATIONAL,
				consts.TERMINALS.SOUTH_TERMINAL,
				consts.TERMINALS.SWING,
				consts.TERMINALS.US,
				consts.TERMINALS.USA
			]
		} as IMapState,
		popup: {
			closeButton: true,
			minWidth: 250,
			maxWidth: 400
		},
		nonPassengerAreas: [
			consts.CLASSIFICAIONS.ADMINISTRATION,
			consts.CLASSIFICAIONS.BACKOFHOUSE,
			consts.CLASSIFICAIONS.INDUSTRIAL
		],
		radius: 0.05,
		showNonPassengerAreas: false,
		styles: {
			roads: {
				color: consts.COLORS.GREY,
				lineCap: 'butt',
				weight: 5
			}
		},
		terminalMapping: {
			'Domestic': [
				consts.TERMINALS.DOMESTIC,
				consts.TERMINALS.LINK
			],
			'International': [
				consts.TERMINALS.ARRIVALS,
				consts.TERMINALS.LINK,
				consts.TERMINALS.INTERNATIONAL,
				consts.TERMINALS.SWING
			],
			'US': [
				consts.TERMINALS.ARRIVALS,
				consts.TERMINALS.US,
				consts.TERMINALS.USA,
				consts.TERMINALS.SWING
			]
		},
		urls: {
			icons: '/Areas/YVR/Content/images/map',
			gates: {
				service: `/${i18n.getCurrentLanguage()}/_api/Gates`,
				select: [
					'Latitude',
					'Longitude',
					'PoiNameLocID'
				]
			},
			gateAreas: {
				service: `/${i18n.getCurrentLanguage()}/_api/GateAreas`,
				select: [
					'Latitude',
					'Longitude',
					'PoiNameLocID'
				]
			},
			locations: {
				service: `/${i18n.getCurrentLanguage()}/_api/Facilities`,
				filter: [
					`${consts.PROPERTIES.CATEGORY} eq 'STR_FOOD_DRINK'`,
					`${consts.PROPERTIES.CATEGORY} eq 'STR_PARKING'`,
					`${consts.PROPERTIES.CATEGORY} eq 'STR_SHOPS'`,
					`${consts.PROPERTIES.CATEGORY} eq 'LOC_PERSONAL_SERVICES'`,
					`${consts.PROPERTIES.CATEGORY} eq 'STR_FACILITIES'`,
					`${consts.PROPERTIES.CATEGORY} eq 'STR_TRANSPORTATION'`,
					`${consts.PROPERTIES.SUBCATEGORY} eq 'STR_TAXIS'`
				],
				select: [
					'Description',
					'FacilityId',
					'IsOpen24Hours',
					'IsOpenNow',
					'Latitude',
					'Longitude',
					'NextOpenOrClosedTime',
					'PoiBuildingLevel',
					'PoiCategory',
					'PoiKeywords',
					'PoiID',
					'PoiLogo',
					'PoiMapImportance',
					'PoiMapIconURL',
					'PoiNameLocID',
					'PoiSubCategory',
					'PoiTerminal',
					'StoreFront_4x3_2x_Url',
					'StoreFront_4x3_Url',
					'SubCategoryDescription'
				]
			},
			rooms: {
				Elevators: '/Areas/YVR/Scripts/Data/Rooms/elevators.geojson.json',
				Escalators: '/Areas/YVR/Scripts/Data/Rooms/escalators.geojson.json',
				Roads: '/Areas/YVR/Scripts/Data/Rooms/roads.geojson.json',
				ParkingLots: '/Areas/YVR/Scripts/Data/Rooms/parkinglots.geojson.json',
				BaseLayer: '/Areas/YVR/Scripts/Data/Rooms/terminal.buildings.geojson.json',
				1: '/Areas/YVR/Scripts/Data/Rooms/1.geojson.json',
				2: '/Areas/YVR/Scripts/Data/Rooms/2.geojson.json',
				3: '/Areas/YVR/Scripts/Data/Rooms/3.geojson.json',
				4: '/Areas/YVR/Scripts/Data/Rooms/4.geojson.json'
			}
		},
		zoom: {
			initial: 16,
			min: 15,
			max: 18
		},
		zoomControl: {
			position: 'bottomleft'
		}
	};

	//#endregion


	//#region Data

	/**
	 * Download the room data. Each level has its own room.
	 * TODO: Refactor this once the service layer for rooms is in place.
	 * @param cache An object to store the downloaded room, facility and GeoJSON data.
	 * @return {Promise}
	 */
	export function getFullMapData(cache: any): JQueryPromise<any> {
		const rooms = config.urls.rooms;

		return $.when(
			$.getJSON(rooms.BaseLayer, data => {
				cache[rooms.BaseLayer] = data;
			}),
			$.getJSON(rooms[1], data => {
				cache[rooms[1]] = data;
			}),
			$.getJSON(rooms[2], data => {
				cache[rooms[2]] = data;
			}),
			$.getJSON(rooms[3], data => {
				cache[rooms[3]] = data;
			}),
			$.getJSON(rooms[4], data => {
				cache[rooms[4]] = data;
			}),
			$.getJSON(rooms.Roads, data => {
				cache[rooms.Roads] = data;
			}),
			$.getJSON(rooms.ParkingLots, data => {
				cache[rooms.ParkingLots] = data;
			}),
			$.getJSON(rooms.Elevators, data => {
				cache[rooms.Elevators] = data;
			}),
			//$.getJSON(rooms.Escalators, data => {
			//	cache[rooms.Escalators] = data;
			//}),
			$.getJSON(`${config.urls.locations.service}?$filter=${config.urls.locations.filter.join(' or ')}&$select=${config.urls.locations.select.join(',')}`, data => {
				cache.locations = data.value;
			}),
			$.getJSON(`${config.urls.gates.service}?$select=${config.urls.gates.select.join(',')}`, data => {
				cache.gates = data.value;
			}),
			$.getJSON(`${config.urls.gateAreas.service}?$select=${config.urls.gateAreas.select.join(',')}`, data => {
				cache.gateAreas = data.value;
			})
		).fail(() => {
			window.console.error('[map][renderFloorLevel] Room and location data retrieval failed.');
		});
	}

	//#endregion


	//#region Facility

	/**
	 * Filter the featuers by property, terminal and level
	 * @param  {Object} feature  The GeoJSON feature from Leaflet
	 * @param  {Ojbect} state    Current state of the map.
	 * @param  {String} property The name of the property to filter on
	 * @param  {String} value    The value of the property
	 * @return {Boolean}         Whether or not to show the feature
	 */
	export function filterFeature(feature, state: IMapState, property: string, value: string | string[]) {
		// Show feature if:
		// 1. It has a property with a specific value.
		// 2. It is in the current terminal.
		// 3. It is on the current level.
		return property &&
			value &&
			value.includes(feature[property]) &&
			state.terminals.includes(feature[consts.PROPERTIES.TERMINAL]) &&
			feature[consts.PROPERTIES.BUILDING_LEVEL] === parseInt(state.level, 10);
	}


	/**
	 * Determines the feature color based on the terminal and sector.
	 * @param  {Object} feature   A Leaflet feature element.
	 * @param  {Object} terminals The current state of the map.
	 * @return {String}           A HEX color code.
	 */
	export function getFeatureColor(feature: GeoJSON.Feature<GeoJSON.Polygon>, terminals: string[], currentConfig) {
		// If road or parkade, show the road colour
		if (feature && (feature.properties.ROAD_CLASS || feature.properties.BasemapClassification === 'parkade')) {
			return config.colors.road;
		}

		const terminal: string = feature.properties.Sector;

		if (!currentConfig.showNonPassengerAreas && config.nonPassengerAreas.includes(feature.properties.Classification)) {
			return config.colors.inactive.before;
		}

		// If the feature is in the current terminal, switch to use the active color palette.
		const colorSet = terminals && !terminals.includes(terminal) ? config.colors.inactive : config.colors.active;

		return feature.properties.Security === consts.SECURITY.AFTER ? colorSet.after : colorSet.before;
	}


	/**
	 * Determines the feature width based on the zoom level.
	 * @param feature The GeoJSON feature.
	 * @param zoom The current zoom level.
	 */
	export function getWeightOnZoom(feature: GeoJSON.Feature<GeoJSON.LineString>, zoom: number): number {
		if ((feature.properties as IRoad).ROAD_CLASS) {
			if (16 <= zoom && zoom < 17) {
				return 8;
			} else if (17 <= zoom && zoom < 18) {
				return 15;
			} else if (18 <= zoom && zoom < 19) {
				return 20;
			}
		}

		return 5;
	}


	/**
	 * Return an icon size based on the current zoom level.
	 * @param zoom The current zoom level.
	 */
	export function getSizeOnZoom(zoom: number): number {
		zoom = Math.ceil(zoom);

		let size;

		switch (zoom) {
			case 16:
				size = 18;
				break;
			case 17:
				size = 20;
				break;
			case 18:
				size = 22;
				break;
			default:
				size = 16;
				break;
		}

		return size;
	}


	/**
	 * Checks if layer exists, removes it from map.
	 * @param map The Leaflet map instance.
	 * @param layer The layer on the map.
	 */
	export function removeLayer(map: L.Map, layer: L.Layer) {
		if (layer) {
			map.removeLayer(layer);
		}
	}


	/**
	 * Reset room colors based on security. This only affects the current layer, so this needs to be
	 * run on every base layer change.
	 * @param {Map} map The Leaflet map instance.
	 */
	export function resetRoomStyle(map: L.Map, terminals: string[], currentConfig) {
		const zoom = map.getZoom();
		const opacity = zoom <= 17 ? 1 : 0.8;

		map.eachLayer(layer => {
			if ((layer as any).setStyle && (layer as any).feature) {
				(layer as any).setStyle({
					weight: getWeightOnZoom((layer as any).feature, zoom),
					color: getFeatureColor((layer as any).feature, terminals, currentConfig),
					fillOpacity: opacity
				});
			}
		});
	}

	//#endregion


	//#region Icons

	/**
	 * Create a marker icon for Leaflet.
	 * @returns {Icon} A location icon for Leaflet maps.
	 */
	export function createIcon(selected): L.Icon {
		return L.icon({
			className: `${config.classNames.marker}--location`,
			iconUrl: selected ? `${config.urls.icons}/marker-selected.svg` : `${config.urls.icons}/marker.svg`,
			iconSize: [19, 25],
			iconAnchor: [10, 25],
			popupAnchor: [0, -20]
		});
	}


	/**
	 * Create a marker icon for Leaflet.
	 * @returns {Icon} A location icon for Leaflet maps.
	 */
	export function createSelectedIcon(): L.Icon {
		return L.icon({
			className: `${config.classNames.marker}--location`,
			iconUrl: `${config.urls.icons}/marker-selected.svg`,
			iconSize: [19, 25],
			iconAnchor: [10, 25],
			popupAnchor: [0, -20]
		});
	}


	/**
	 * Create a marker icon for Leaflet.
	 * @returns {Icon} A location icon for Leaflet maps.
	 */
	export function createStepIcon(step: IItineraryStep, currentFloor: number, number: number): L.DivIcon {
		const height = 48;
		const width = 34;

		let className = `${config.classNames.marker}--step yvr-itinerary-icon`;

		if (step.Facility.PoiBuildingLevel !== currentFloor) {
			className += ' yvr-itinerary-icon--selected';
		}

		return L.divIcon({
			className,
			html: `${number}`,
			iconSize: [width, height],
			iconAnchor: [width / 2, height],
			popupAnchor: [0, -height]
		});
	}


	/**
	 * Create a marker icon for Leaflet.
	 * @returns {Icon} A location icon for Leaflet maps.
	 */
	export function createUnselectedStepIcon(number: number): L.DivIcon {
		return L.divIcon({
			className: `${config.classNames.marker}--step yvr-itinerary-icon yvr-itinerary-icon--unselected`,
			html: `${number}`,
			iconSize: [34, 48],
			iconAnchor: [17, 48],
			popupAnchor: [0, -48]
		});
	}


	/**
	 * Create a facility icon to use for both the cluster group and individual icons.
	 * @param name The name of the type of facility.
	 * @param size The size of the icon. Default is 16.
	 */
	export function createFacilityIcon(name: string, size = 16, feature: any = {}): L.Icon {
		let iconUrl: string;

		// Use PoiMapIconURL if the field is filled out.
		//if (feature.PoiMapIconURL && feature.PoiMapIconURL !== '') {
		//	iconUrl = feature.PoiMapIconURL;
		//}

		// SPECIAL CASE: Baggage carousels have their own icons.
		if (name === consts.SUBCATEGORIES.BAGGAGE && feature.PoiNameLocID) {
			const matches = feature.PoiNameLocID.match(consts.REGEX.CAROUSEL_NUMBER);

			if (matches && matches.length && matches[1] > 0) {
				iconUrl = `${config.urls.icons}/baggage-carousels/${matches[1]}.svg`;
			} else {
				iconUrl = `${config.urls.icons}/${name}.svg`;
			}
		} else {
			iconUrl = `${config.urls.icons}/${name}.svg`;
		}

		return L.icon({
			className: `${config.classNames.marker}--facility ${config.classNames.marker}--${name}`,
			iconUrl: iconUrl,
			iconSize: [size, size],
			iconAnchor: [size / 2, size / 2]
		});
	}


	/**
	 * Get the width of a character in pixels.
	 * @param char The character to measure.
	 * @returns The width of the character in pixels.
	 */
	function getCharacterWidth(char: string): number {
		if (char === '-') {
			return 2;
		} else if (char === ',') {
			return 2;
		} else if (char.match(/\s/i)) {
			return 2;
		}

		return 9;
	}


	/**
	 * Create an array of ranges.
	 * Sourced from: https://github.com/runvnc/get-ranges
	 * @param An array of numbers.
	 * @return An array of string ranges.
	 */
	function createRanges(array: number[]): string[] {
		let ranges = [];

		// Loop through each number in the array
		for (let i = 0; i < array.length; i++) {
			let start = array[i];
			let end = start;

			// Continue to loop if the number previous is before the current number
			while (array[i + 1] - array[i] === 1) {
				// Increment the index if the numbers sequential
				end = array[i + 1];
				i++;
			}

			ranges.push(start === end ? `${start}` : `${start}-${end}`);
		}

		return ranges;
	}


	/**
	 * Create a marker icon for gates.
	 * @param   {Object} cluster The marker cluster for gates.
	 * @returns {Icon}           A gate icon for Leaflet maps.
	 */
	export function createGateClusterIcon(cluster) {
		const defaultWidth = 21;

		let html = '';
		let width = defaultWidth;

		const markers = cluster.getAllChildMarkers();

		if (markers && markers.length) {
			// Map the gate numbers
			let gates = markers.map((marker) => marker.options.icon.options.html.replace(/\s/gi, ''));

			// Remove duplicates
			gates = gates.filter((gate, index, array) => array.indexOf(gate) === index);
			gates = gates.sort();

			html = createRanges(gates).join(', ');
			width = 2;

			for (let i = 0; i < html.length; i++) {
				width += getCharacterWidth(html[i]);
			}

			if (width < defaultWidth) {
				width = defaultWidth;
			}
		}

		return L.divIcon({
			className: `${config.classNames.marker}--gate ${config.classNames.marker}--gate-cluster`,
			html,
			iconSize: [width, 20]
		});
	}


	/**
	 * Create a marker icon for gates.
	 * @param   {Object} feature An individual room feature.
	 * @returns {Icon}           A gate icon for Leaflet maps.
	 */
	export function createGateIcon(feature) {
		const matches = feature.PoiNameLocID.match(consts.REGEX.GATE_NUMBER);
		const html = matches ? matches[1] : feature.PoiNameLocID;

		return L.divIcon({
			className: `${config.classNames.marker}--gate`,
			html,
			iconSize: [20, 20]
		});
	}


	/**
	 * Create a marker icon for gate areas.
	 * @param   {Object} feature An individual room feature.
	 * @returns {Icon}           A gate icon for Leaflet maps.
	 */
	export function createGateAreaIcon(feature) {
		return L.divIcon({
			className: `${config.classNames.marker}--gate-area`,
			html: feature.PoiNameLocID,
			iconSize: [60, 16]
		});
	}

	//#endregion


	/*#region Layers */

	/**
	 * Add or remove a layer based on map importance and the current zoom.
	 * @param zoomToShow The zoom level to add or remove the layer.
	 */
	export function resetOneLayerOnImportance(map: L.Map, layer: L.Layer, zoomToShow: number): void {
		map.on('zoomend', () => {
			const zoom = map.getZoom();

			if (Math.ceil(zoom) >= zoomToShow) {
				map.addLayer(layer);
			} else {
				map.removeLayer(layer);
			}
		});
	}

	/*#endregion */


	//#region Geography

	/**
	 * Create a small bounds object based on a center and radius.
	 * @param  {Array}        center An array with latitude and longitude.
	 * @param  {Float}        radius The distance around the center to create a bounds.
	 * @return {LatLngBounds}        A Leaflet bound object for Leaflet.
	 */
	export function createBounds(center, radius) {
		const southWest = L.latLng(center[0] - radius, center[1] - radius);
		const northEast = L.latLng(center[0] + radius, center[1] + radius);

		return L.latLngBounds(southWest, northEast);
	}

	//#endregion
}
